// In-app camera capture via getUserMedia, with a graceful fallback to the native
// file picker (capture=environment) when the live camera isn't available
// (older browsers, denied permission, or insecure context).

type Cb = (blob: Blob) => void;

const ICON = (d: string, sz = 22) =>
  `<svg width="${sz}" height="${sz}" viewBox="0 0 24 24" fill="none"><path d="${d}" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

export function openCamera(mount: HTMLElement, onCapture: Cb, onCancel: () => void) {
  const md = navigator.mediaDevices;
  if (!md || !md.getUserMedia) return fileFallback(onCapture, onCancel);

  let stream: MediaStream | null = null;
  let facing: 'environment' | 'user' = 'environment';

  mount.hidden = false;
  mount.innerHTML = `
    <div class="cam-wrap">
      <video autoplay playsinline muted></video>
      <svg class="cam-grid" viewBox="0 0 100 100" preserveAspectRatio="none">
        <path d="M33 0V100M67 0V100M0 33H100M0 67H100" stroke="rgba(255,255,255,.22)" stroke-width="0.4"/>
      </svg>
      <div class="cam-frame"></div>
      <div class="cam-tip"><span class="dot"></span> Align the spread in the frame</div>
      <button type="button" class="cam-close" aria-label="Close">${ICON('M6 6l12 12M18 6L6 18', 20)}</button>
    </div>
    <div class="cam-bar">
      <button type="button" class="cam-icon-btn" id="cam-upload" aria-label="Upload instead">${ICON('M12 16V4M7 9l5-5 5 5M5 20h14')}</button>
      <button type="button" class="cam-shutter" aria-label="Capture"></button>
      <button type="button" class="cam-icon-btn" id="cam-flip" aria-label="Flip camera">${ICON('M20 11a8 8 0 10-2 6m2 0v-5h-5')}</button>
    </div>`;

  const video = mount.querySelector('video') as HTMLVideoElement;

  async function start() {
    stop();
    try {
      stream = await md.getUserMedia({ video: { facingMode: facing }, audio: false });
      video.srcObject = stream;
    } catch {
      cleanup();
      return fileFallback(onCapture, onCancel);
    }
  }
  function stop() { if (stream) { stream.getTracks().forEach((t) => t.stop()); stream = null; } }
  function cleanup() { stop(); mount.innerHTML = ''; mount.hidden = true; }

  mount.querySelector('.cam-close')!.addEventListener('click', () => { cleanup(); onCancel(); });
  mount.querySelector('#cam-flip')!.addEventListener('click', () => { facing = facing === 'environment' ? 'user' : 'environment'; start(); });
  mount.querySelector('#cam-upload')!.addEventListener('click', () => { cleanup(); fileFallback(onCapture, onCancel); });
  mount.querySelector('.cam-shutter')!.addEventListener('click', () => {
    const w = video.videoWidth, h = video.videoHeight;
    if (!w || !h) return;
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    canvas.getContext('2d')!.drawImage(video, 0, 0, w, h);
    canvas.toBlob((blob) => { cleanup(); if (blob) onCapture(blob); else onCancel(); }, 'image/jpeg', 0.92);
  });

  start();
}

// Native fallback: opens the OS camera/file picker.
function fileFallback(onCapture: Cb, onCancel: () => void) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.setAttribute('capture', 'environment');
  input.style.display = 'none';
  document.body.appendChild(input);
  input.addEventListener('change', () => {
    const f = input.files && input.files[0];
    input.remove();
    if (f) onCapture(f); else onCancel();
  });
  // If the user dismisses without choosing, there's no reliable event; harmless.
  input.click();
}
